import { inject, Injectable, signal, computed, effect } from '@angular/core';
import { Observable, of, tap, BehaviorSubject } from 'rxjs';
import { ISelectOption } from '../../shared/models/select-option.interface';
import { CustomerService } from './customer.service';
import { StorageService } from './storage.service';
import { ICustomerSelectionService } from './interfaces/customer-selection.interface';


@Injectable({ providedIn: 'root' })
export class CustomerSelectionService implements ICustomerSelectionService {
  private readonly customerService = inject(CustomerService);
  private readonly storageService = inject(StorageService);
  
  private readonly customerChangedSubject = new BehaviorSubject<string | null>(null);
  readonly customerChanged$ = this.customerChangedSubject.asObservable();

  private readonly TTL = 5 * 60 * 1000;
  private readonly customerCache = new Map<string, { data: ISelectOption[]; timestamp: number }>();
  
  private readonly selectedCustomerSignal = signal<ISelectOption | undefined>(undefined);
  private readonly customersOptionsSignal = signal<ISelectOption[]>([]);
  private readonly loadingCustomersSignal = signal<boolean>(false);

  readonly selectedCustomer = this.selectedCustomerSignal.asReadonly();
  readonly customersOptions = this.customersOptionsSignal.asReadonly();
  readonly loadingCustomers = this.loadingCustomersSignal.asReadonly();

  readonly shouldShowCustomerSelect = computed(() => 
    !this.loadingCustomersSignal() && this.customersOptionsSignal().length > 1
  );

  readonly hasSelectedCustomer = computed(() => 
    !!this.selectedCustomerSignal()
  );

  private readonly persistEffect = effect(() => {
    const customer = this.selectedCustomerSignal();
    if (customer) {
      this.storageService.set('quironSelectedCustomer', customer);
    }
  });

  constructor() {
    this.initializeCustomerSelection();
  }

  /**
   * Seleciona um cliente e atualiza o estado
   * @param customer Cliente selecionado
   */
  selectCustomer(customer: ISelectOption): void {
    this.selectedCustomerSignal.set(customer);
    this.customerChangedSubject.next(customer.value);
  }

  /**
   * Carrega a lista de clientes disponíveis com cache TTL
   * @param forceRefresh Força a atualização ignorando o cache
   * @returns Observable com lista de clientes
   */
  loadAvailableCustomers(forceRefresh: boolean = false): Observable<ISelectOption[]> {
    // 🎯 CACHE PATTERN (como GeometryProcessorService)
    if (!forceRefresh) {
      const cached = this.getFromCache('available-customers');
      if (cached) {
        this.customersOptionsSignal.set(cached);
        return of(cached);
      }
    }

    this.loadingCustomersSignal.set(true);

    return this.customerService.loadAvailableCustomersForSelection().pipe(
      tap(customerOptions => {
        this.setInCache('available-customers', customerOptions);
        this.customersOptionsSignal.set(customerOptions);
        this.loadingCustomersSignal.set(false);

        const selectedCustomer = this.selectedCustomerSignal();
        const refreshedSelectedCustomer = customerOptions.find(option => option.value === selectedCustomer?.value);
        if (refreshedSelectedCustomer) {
          this.selectCustomer(refreshedSelectedCustomer);
        } else if (!selectedCustomer && customerOptions.length > 0) {
          this.selectCustomer(customerOptions[0]);
        }
      })
    );
  }

  /**
   * Define a lista de opções de clientes
   * @param options Lista de opções
   */
  setCustomersOptions(options: ISelectOption[]): void {
    this.customersOptionsSignal.set(options);
    this.setInCache('available-customers', options);
  }

  /**
   * Limpa a seleção atual de cliente
   */
  clearSelection(): void {
    this.selectedCustomerSignal.set(undefined);
    this.storageService.remove('quironSelectedCustomer');
  }

  /**
   * Restaura a seleção persistida do localStorage
   */
  private restorePersistedCustomer(): void {
    const persisted = this.storageService.get<ISelectOption>('quironSelectedCustomer');
    if (persisted) {
      this.selectedCustomerSignal.set(persisted);
    }
  }


  private initializeCustomerSelection(): void {
    const persisted = this.storageService.get<ISelectOption>('quironSelectedCustomer');
    if (persisted) {
      this.selectedCustomerSignal.set(persisted);
      this.customerChangedSubject.next(persisted.value);
    }

    this.loadAvailableCustomers().subscribe({
      next: (customers) => {
        if (!this.selectedCustomerSignal() && customers.length > 0) {
          this.selectCustomer(customers[0]);
        }
        // Se há apenas um cliente, garante que ele esteja selecionado
        else if (customers.length === 1) {
          this.selectCustomer(customers[0]);
        }
      },
      error: (error) => {
        console.error('❌ CustomerSelection: Erro ao carregar clientes', error);
        this.handleLoadError(error);
      }
    });
  }

  /**
   * Obtém dados do cache com validação TTL
   * @param key Chave do cache
   * @returns Dados em cache ou null
   */
  private getFromCache(key: string): ISelectOption[] | null {
    const cached = this.customerCache.get(key);
    if (!cached) return null;

    if (Date.now() - cached.timestamp > this.TTL) {
      this.customerCache.delete(key);
      return null;
    }

    return cached.data;
  }

  /**
   * Armazena dados no cache com timestamp
   * @param key Chave do cache
   * @param data Dados a serem armazenados
   */
  private setInCache(key: string, data: ISelectOption[]): void {
    this.customerCache.set(key, {
      data,
      timestamp: Date.now()
    });
  }

  /**
   * Limpa todo o cache de clientes
   */
  clearCache(): void {
    this.customerCache.clear();
  }

  /**
   * Limpa completamente todos os dados do serviço (cache, seleção e estado)
   */
  clearAllData(): void {
    this.clearCache();
    this.clearSelection();
    this.customersOptionsSignal.set([]);
    this.loadingCustomersSignal.set(false);
    this.customerChangedSubject.next(null);
  }


  private handleLoadError(error: any): void {
    const fallbackCustomers: ISelectOption[] = [
      { label: 'Cliente Padrão', value: 'default-customer' }
    ];

    this.customersOptionsSignal.set(fallbackCustomers);

    if (!this.selectedCustomerSignal()) {
      this.selectCustomer(fallbackCustomers[0]);
    }
  }

  /**
   * Obtém o ID do cliente selecionado
   * @returns ID do cliente ou null
   */
  getSelectedCustomerId(): string | null {
    return this.selectedCustomerSignal()?.value || null;
  }

  /**
   * Verifica se um cliente específico está selecionado
   * @param customerId ID do cliente para verificar
   * @returns true se o cliente estiver selecionado
   */
  isCustomerSelected(customerId: string): boolean {
    return this.selectedCustomerSignal()?.value === customerId;
  }
}
